import { useProjectTitle } from "components/pages/ProfessionalSpace/contexts/ProjectTitle";
import classes from "./classes.module.scss";
import PosterContainer from "./PosterContainer";
import ProjectInfoInputs from "./ProjectInfoInputs";
import { useCallback, useEffect, useState } from "react";
import { EProjectTheme } from "common/enums/ProjectTheme/ProjectTheme";
import ProjectConfigResponseResource from "common/resources/Project/Config/ProjectConfig/ProjectConfigResponseResource";
import { EAnchorStatus } from "common/enums/Anchor/EAnchorStatus";
import { container } from "tsyringe";
import ConfigService from "services/projects/ConfigService";
import Form, { useForm } from "components/materials/Form";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import { EToastType, useToast } from "contexts/Toast";
import assert from "assert";
import ProjectService from "services/ProjectService";
import PosterService from "services/projects/PosterService";
import { EProjectPurpose } from "common/enums/ProjectPurpose";
import { EProjectFormat } from "common/enums/ProjectFormat/ProjectFormat";
import { ELang } from "common/enums/User/ELang";
import ProjectUpdateRequestResource from "common/resources/Project/ProjectUpdateRequestResource";
import { I18nTextResource } from "common/resources/I18nText/I18nTextResource";
import ProjectConfigRequestResource from "common/resources/Project/Config/ProjectConfig/ProjectConfigRequestResource";

const projectConfigService = container.resolve(ConfigService);
const projectService = container.resolve(ProjectService);
const posterService = container.resolve(PosterService);

type FormData = {
	[key: string]: unknown;
};

export default function InformationContent() {
	const { showToast } = useToast();
	const { clearErrors, handleError, errors, formRef } = useForm();
	const { project, title, fetchProject } = useProjectTitle();
	const [selectedTheme, setSelectedTheme] = useState<EProjectTheme | null>(null);
	const [projectConfig, setProjectConfig] = useState<ProjectConfigResponseResource | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [hasDataChanged, setHasDataChanged] = useState(false);
	const isProjectAnchored = project.scenario?.anchor?.status === EAnchorStatus.VERIFIED_ON_CHAIN;

	const onDataChange = useCallback(() => {
		if (hasDataChanged) return;
		setHasDataChanged(true);
	}, [hasDataChanged]);

	const onThemeSelected = useCallback((theme: EProjectTheme) => {
		setSelectedTheme(theme);
		setHasDataChanged(true);
	}, []);

	const validateProjectData = useCallback(
		(formData: FormData) => {
			assert(selectedTheme, "No selected theme");

			const projectResource = ProjectUpdateRequestResource.hydrate<ProjectUpdateRequestResource>({
				title: title,
				pitch: project.pitch,
				format: formData["format"] as EProjectFormat,
				theme: selectedTheme,
				purposes: [EProjectPurpose.AUTHENTICITY_PROTECTION],
				lang: I18n.getLang().toLocaleUpperCase() as ELang,
				discord: project.discord ?? undefined,
				instagram: project.instagram ?? undefined,
				x: project.x ?? undefined,
				linkedin: project.linkedin ?? undefined,
			});

			return projectResource.validateOrReject();
		},
		[selectedTheme, title, project],
	);

	const validateProjectConfig = useCallback(
		async (formData: FormData) => {
			assert(projectConfig, "No project config");
			const titleResource = I18nTextResource.hydrateArray<I18nTextResource>([{ lang: I18n.getLang(), text: title }]);
			const subtitleResource = formData["subtitle"]
				? I18nTextResource.hydrateArray<I18nTextResource>([{ lang: I18n.getLang(), text: formData["subtitle"] as string }])
				: undefined;
			const descriptionResource = I18nTextResource.hydrateArray<I18nTextResource>([{ lang: I18n.getLang(), text: formData["description"] as string }]);
			const developmentStageResource = formData["developmentStage"]
				? I18nTextResource.hydrateArray<I18nTextResource>([{ lang: I18n.getLang(), text: formData["developmentStage"] as string }])
				: undefined;
			const projectNeedsResource = formData["projectNeeds"]
				? I18nTextResource.hydrateArray<I18nTextResource>([{ lang: I18n.getLang(), text: formData["projectNeeds"] as string }])
				: undefined;

			const projectConfigResource = ProjectConfigRequestResource.hydrate<ProjectConfigRequestResource>({
				title: titleResource,
				subtitle: subtitleResource,
				description: descriptionResource,
				developmentStage: developmentStageResource,
				projectNeeds: projectNeedsResource,
				contactEmail: projectConfig.contactEmail ?? undefined,
			});

			return projectConfigResource.validateOrReject();
		},
		[title, projectConfig],
	);

	const onSubmit = useCallback(
		async (formData: FormData) => {
			try {
				assert(projectConfig, "No project config");

				clearErrors();
				setIsLoading(true);

				const projectResource = await validateProjectData(formData);
				const projectConfigResource = await validateProjectConfig(formData);

				if (!isProjectAnchored) {
					await projectService.updateProject(project.id, projectResource, {});

					const newPoster = (formData["newPoster"] as FileList)?.[0];
					if (newPoster) {
						await posterService.updatePoster(project.id, newPoster);
					}
				}

				await projectConfigService.update(project.id, projectConfig.id, projectConfigResource);

				setIsLoading(false);
				showToast({ message: I18n.asset.pages.professional_space.tabs.information.saved, type: EToastType.SUCCESS });
			} catch (error) {
				setIsLoading(false);
				handleError(error);
			}
		},
		[project, clearErrors, handleError, setIsLoading, showToast, projectConfig, isProjectAnchored, validateProjectData, validateProjectConfig],
	);

	useEffect(() => {
		projectConfigService
			.getByProjectId(project.id)
			.then((config) => {
				setProjectConfig(config);
				setSelectedTheme(config.project.theme);
			})
			.catch(console.error);
	}, [project.id]);

	return (
		<Form onSubmit={onSubmit} errors={errors} className={classes["root"]} onChange={onDataChange} formRef={formRef}>
			<div className={classes["header-container"]}>
				<Typography type="h3" weight="bold">
					{I18n.asset.pages.professional_space.tabs.information.sub_tabs.content}
				</Typography>
				<Button type="submit" variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} isLoading={isLoading} disabled={!hasDataChanged}>
					{I18n.asset.pages.professional_space.tabs.information.save}
				</Button>
			</div>

			<div className={classes["form"]}>
				<PosterContainer project={project} onPosterChange={fetchProject} />
				<ProjectInfoInputs selectedTheme={selectedTheme} onSelect={onThemeSelected} config={projectConfig} isProjectAnchored={isProjectAnchored} />
			</div>

			<Button type="submit" variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} isLoading={isLoading} disabled={!hasDataChanged}>
				{I18n.asset.pages.professional_space.tabs.information.save}
			</Button>
		</Form>
	);
}
